import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { CampaignActionBar } from '../components/ContactAgentCampaignList';
import { ContactAgentMetricCard } from '../components/ContactAgentMetricCard';
import { formatCampaignMoney } from '../components/ContactAgentMetricCard.utils';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentCampaignTemplatesSection } from '../components/ContactAgentCampaignTemplatesSection';
import { CampaignUnreachablePanel } from '../components/CampaignUnreachablePanel';
import { CampaignRepliesPanel } from '../components/CampaignRepliesPanel';
import { CampaignTestSendPanel } from '../components/CampaignTestSendPanel';
import { channelDataLabel } from '../api/campaignWizardRules';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { contactAgentApi } from '../api/contactAgentApi';
import { CAMPAIGN_BOARD_LABEL, campaignBoardStatus } from '../api/campaignActions';
import { contactAgentError } from '../api/contactAgentScope';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { campaignChannelDisplayLabel, campaignEndsAtDateLabel } from '../api/campaignDisplay';
import {
  CAMPAIGN_STATUS_LABEL,
  DELIVERY_MODE_LABEL,
  EXCLUSION_REASON_LABEL,
  PURPOSE_LABEL,
  type CampaignAudiencePage,
  type CampaignAudienceStatus,
  type CampaignDetailMetrics,
  type CampaignRecord,
  type UnreachableAudiencePage,
} from '@workspace/types';
import { CAMPAIGNS_HUB_PATH } from '../api/campaignPaths';

export function ContactAgentCampaignDetailPage() {
  const navigate = useNavigate();
  const campaignId = String(useParams().campaignId ?? '').slice(0, 64);
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const currentUser = useAppSelector(selectCurrentUser);
  const actorRole = currentUser?.role ?? 'USER';
  const [campaign, setCampaign] = useState<CampaignRecord | null>(null);
  const [metrics, setMetrics] = useState<CampaignDetailMetrics | null>(null);
  const [audience, setAudience] = useState<CampaignAudiencePage | null>(null);
  const [unreachable, setUnreachable] = useState<UnreachableAudiencePage | null>(null);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!ready || !campaignId) {
      return;
    }
    let cancelled = false;
    setError('');
    void Promise.all([
      contactAgentApi.getCampaign(campaignId),
      contactAgentApi.getCampaignMetrics(campaignId),
      contactAgentApi.getAudience(campaignId).catch(() => null),
      contactAgentApi.getUnreachable(campaignId).catch(() => null),
    ])
      .then(([loaded, detail, audiencePage, unreachablePage]) => {
        if (cancelled) {
          return;
        }
        setCampaign(loaded);
        setMetrics(detail);
        setAudience(audiencePage);
        setUnreachable(unreachablePage);
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId, campaignId, refreshKey]);

  if (!campaign) {
    return (
      <ContactAgentPage>
        <ContactAgentPageHeader
          eyebrow='Campañas'
          backTo={CAMPAIGNS_HUB_PATH}
          title={error ? 'No se encontró' : 'Campaña'}
          description={error || 'Cargando tablero...'}
          toolbar={
            isSuperAdmin ? (
              <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
            ) : null
          }
        />
      </ContactAgentPage>
    );
  }

  const board = campaignBoardStatus(campaign);
  const cobranzas = metrics?.cobranzas;
  const isNotification = campaign.deliveryMode === 'NOTIFICATION';
  const showRepliesTab = isPushNotificationCampaign(campaign);
  const showFsmBoard = !isNotification;
  const headerDescription = showRepliesTab
    ? 'Tablero de envíos y respuestas de esta campaña.'
    : showFsmBoard
      ? campaign.purpose === 'COBRANZAS'
        ? 'Tablero de cobranzas y conversaciones de esta campaña.'
        : 'Tablero de conversaciones de esta campaña de aviso.'
      : 'Tablero de envíos de esta notificación.';

  const audienceSection = audience ? (
        <section className='space-y-3'>
          <h2 className='text-lg font-semibold'>Audiencia</h2>
          <p className='text-sm text-muted-foreground'>
            Revision {audience.revision}
            {audience.frozenAt ? ' · congelada' : ' · editable en borrador'}. La pertenencia no autoriza el envio.
          </p>
          <div className='grid gap-4 sm:grid-cols-3'>
            <ContactAgentMetricCard title='Elegibles' value={String(audience.eligibleCount)} />
            <ContactAgentMetricCard title='Diferidos' value={String(audience.deferredCount)} />
            <ContactAgentMetricCard title='Excluidos' value={String(audience.excludedCount)} />
          </div>
          {unreachable ? (
            <p className='text-sm text-muted-foreground'>
              {audience.total} en segmento → {audience.eligibleCount} alcanzables → {unreachable.total} sin{' '}
              {channelDataLabel(campaign.channelKind)}.
            </p>
          ) : null}
          {unreachable ? (
            <CampaignUnreachablePanel
              campaign={campaign}
              page={unreachable}
              canReevaluate={campaign.status === 'DRAFT'}
              onReevaluated={() => setRefreshKey(key => key + 1)}
              onError={setError}
            />
          ) : null}
          <div className='overflow-auto rounded-xl border'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b text-left'>
                  <th className='px-3 py-2'>Contacto</th>
                  <th className='px-3 py-2'>Estado</th>
                  <th className='px-3 py-2'>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {audience.items.map(member => (
                  <tr key={member.id} className='border-b last:border-0'>
                    <td className='px-3 py-2'>
                      {member.contact?.businessName || member.contact?.firstName || member.contactId}
                    </td>
                    <td className='px-3 py-2'>{audienceStatusLabel(member.status)}</td>
                    <td className='px-3 py-2 text-muted-foreground'>
                      {member.exclusionReason ? EXCLUSION_REASON_LABEL[member.exclusionReason] : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
  ) : null;

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        eyebrow='Campañas'
        backTo={CAMPAIGNS_HUB_PATH}
        title={campaign.name}
        description={headerDescription}
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
        actions={
          <div className='flex flex-col items-end gap-2'>
            <div className='flex flex-wrap justify-end gap-2'>
              <Badge variant='secondary'>{PURPOSE_LABEL[campaign.purpose]}</Badge>
              <Badge variant='outline'>{DELIVERY_MODE_LABEL[campaign.deliveryMode]}</Badge>
              <Badge variant='outline'>{campaignChannelDisplayLabel(campaign)}</Badge>
              <Badge variant='outline'>{campaignEndsAtDateLabel(campaign.endsAt)}</Badge>
              <Badge>{CAMPAIGN_STATUS_LABEL[campaign.status]}</Badge>
              <Badge variant='outline'>{CAMPAIGN_BOARD_LABEL[board]}</Badge>
            </div>
            {actorRole !== 'USER' ? (
              <CampaignActionBar
                campaign={campaign}
                onUpdated={updated => {
                  setCampaign(current => (current ? { ...current, ...updated } : updated));
                  setRefreshKey(key => key + 1);
                }}
                onDeleted={() => navigate(CAMPAIGNS_HUB_PATH)}
                onError={setError}
              />
            ) : null}
          </div>
        }
      />

      {error ? <p className='text-destructive'>{error}</p> : null}

      {isNotification && campaign.autoReplyText ? (
        <section className='space-y-2'>
          <h2 className='text-lg font-semibold'>Respuesta automática</h2>
          <p className='text-sm text-muted-foreground'>
            Se envía una sola vez si el destinatario contesta el aviso. BAJA se atiende aparte.
          </p>
          <p className='whitespace-pre-wrap rounded-xl border bg-muted/30 p-4 text-sm'>{campaign.autoReplyText}</p>
        </section>
      ) : null}

      {showRepliesTab ? (
        <Tabs defaultValue='audiencia'>
          <TabsList>
            <TabsTrigger value='audiencia'>Audiencia</TabsTrigger>
            <TabsTrigger value='respuestas'>Respuestas</TabsTrigger>
          </TabsList>
          <TabsContent value='audiencia'>{audienceSection}</TabsContent>
          <TabsContent value='respuestas'>
            <CampaignRepliesPanel campaignId={campaign.id} onError={setError} />
          </TabsContent>
        </Tabs>
      ) : (
        audienceSection
      )}

      <ContactAgentCampaignTemplatesSection campaign={campaign} />

      {campaign.status === 'DRAFT' ? (
        <CampaignTestSendPanel
          actorRole={actorRole}
          channelKind={campaign.channelKind}
          campaignId={campaign.id}
          whatsAppChannelId={campaign.whatsAppChannelId ?? undefined}
          messagingChannelId={campaign.messagingChannelId ?? undefined}
          templateId={campaign.initialTemplateId ?? undefined}
          messageTemplateId={campaign.messageTemplateId ?? undefined}
          templateReadOnly={campaign.channelKind === 'WHATSAPP'}
          variableKeys={
            campaign.channelKind === 'WHATSAPP' &&
            campaign.initialTemplateId &&
            campaign.templateVariableMap?.[campaign.initialTemplateId]
              ? Object.entries(campaign.templateVariableMap[campaign.initialTemplateId]).map(
                  ([key, label]) => ({ key, label }),
                )
              : undefined
          }
        />
      ) : null}

      {metrics && showFsmBoard ? (
        <>
          <section className='space-y-3'>
            <h2 className='text-lg font-semibold'>Conversaciones</h2>
            <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
              <ContactAgentMetricCard title='Iniciadas' value={String(metrics.conversationsStarted)} />
              <ContactAgentMetricCard title='Respondidas' value={String(metrics.conversationsResponded)} />
              <ContactAgentMetricCard title='Sin respuesta' value={String(metrics.conversationsNoResponse)} />
              <ContactAgentMetricCard title='Universo' value={String(metrics.audienceCount)} hint='contactos del publico objetivo' />
            </div>
            <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
              <ContactAgentMetricCard title='Baja' value={String(metrics.optOut)} />
              <ContactAgentMetricCard title='Derivadas' value={String(metrics.handedOff)} />
              {campaign.purpose === 'COBRANZAS' ? (
                <ContactAgentMetricCard title='Pagadas' value={String(metrics.paid)} />
              ) : null}
              <ContactAgentMetricCard title='Agotadas' value={String(metrics.exhausted)} />
            </div>
          </section>

          {campaign.purpose === 'COBRANZAS' && cobranzas ? (
            <section className='space-y-3'>
              <h2 className='text-lg font-semibold'>Cobranzas</h2>
              <p className='text-sm text-muted-foreground'>
                Cartera de quienes esta campaña ya contactó. El universo es el publico objetivo.
              </p>
              <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                <ContactAgentMetricCard
                  title='Cartera contactada'
                  value={String(cobranzas.portfolioLoaded)}
                  hint='facturas de contactados'
                />
                <ContactAgentMetricCard
                  title='Vencido'
                  value={formatCampaignMoney(cobranzas.overdueAmount)}
                />
                <ContactAgentMetricCard
                  title='Recuperado'
                  value={`${(cobranzas.recoveryRate * 100).toFixed(1)}%`}
                  hint={formatCampaignMoney(cobranzas.recoveredAmount)}
                />
                <ContactAgentMetricCard
                  title='Compromisos'
                  value={String(cobranzas.promisesActive)}
                  hint={formatCampaignMoney(cobranzas.promisedAmount)}
                />
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </ContactAgentPage>
  );
}

function isPushNotificationCampaign(campaign: CampaignRecord): boolean {
  return (
    campaign.deliveryMode === 'NOTIFICATION' &&
    (campaign.channelKind === 'SMS' || campaign.channelKind === 'EMAIL')
  );
}

const AUDIENCE_STATUS_LABEL: Record<CampaignAudienceStatus, string> = {
  PENDING: 'Pendiente',
  DEFERRED: 'Diferido',
  QUEUED: 'En cola',
  SENDING: 'Enviando',
  SENT: 'Enviado',
  RESPONDED: 'Respondió',
  OPTED_OUT: 'Baja',
  EXCLUDED: 'No se envía',
  FAILED: 'Falló',
};

function audienceStatusLabel(status: CampaignAudienceStatus): string {
  return AUDIENCE_STATUS_LABEL[status] ?? status;
}
